import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  buildLocalizedUrl,
  detectLanguageFromPath,
  getLanguagePrefix,
  resolveLanguageFromParam,
} from '@/utils/languageRouting.js';

const useLanguageNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const langFromUrl = useMemo(
    () => detectLanguageFromPath(location.pathname),
    [location.pathname],
  );
  const language = useMemo(
    () => resolveLanguageFromParam(langFromUrl || i18n.language || DEFAULT_LANGUAGE),
    [i18n.language, langFromUrl],
  );

  const buildTargetUrl = useCallback(
    (path, options = {}) => {
      const targetLanguage = resolveLanguageFromParam(options.language || language);
      const search = options.preserveSearch ? location.search : options.search;
      const hash = options.preserveHash ? location.hash : options.hash;

      return buildLocalizedUrl(path, targetLanguage, search, hash);
    },
    [language, location.hash, location.search],
  );

  const getLocalizedPath = useCallback((path, options) => buildTargetUrl(path, options), [
    buildTargetUrl,
  ]);

  const navigateWithLanguage = useCallback(
    (path, options = {}) => {
      const { replace, state, ...urlOptions } = options;
      const targetPath = buildTargetUrl(path, urlOptions);
      const currentPath = `${location.pathname}${location.search}${location.hash}`;

      if (targetPath !== currentPath) {
        console.debug('[LANG-NAV]', {
          from: currentPath,
          to: targetPath,
          reason: 'language-aware navigation',
          langFromUrl,
          i18nLang: i18n.language,
        });
        navigate(targetPath, { replace, state });
      }
    },
    [
      buildTargetUrl,
      i18n.language,
      langFromUrl,
      location.hash,
      location.pathname,
      location.search,
      navigate,
    ],
  );

  const languagePrefix = useMemo(() => getLanguagePrefix(language), [language]);

  return {
    navigate: navigateWithLanguage,
    getLocalizedPath,
    languagePrefix,
    language,
  };
};

export default useLanguageNavigation;
